import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, UserX, AlertCircle, CheckCircle, Clock, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useEmployees } from '@/api/queries/employee.queries';
import { useFullFinalList, useInitiateFullFinal } from '@/api/queries/document.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';

const SEPARATION_TYPES = ['Resignation', 'Termination', 'End of Contract', 'Retirement', 'Absconding'];
const STAGE_META = {
  INITIATION:  { label: 'Initiation',   color: 'bg-blue-100 text-blue-700'   },
  CLEARANCE:   { label: 'Clearance',    color: 'bg-amber-100 text-amber-700' },
  COMPUTATION: { label: 'Computation',  color: 'bg-purple-100 text-purple-700'},
  DOCUMENTS:   { label: 'Documents',    color: 'bg-indigo-100 text-indigo-700'},
  PF_STATUTORY:{ label: 'PF & Statutory', color: 'bg-cyan-100 text-cyan-700' },
  CLOSED:      { label: 'Closed',       color: 'bg-green-100 text-green-700' },
};

function InitiateModal({ onClose }) {
  const navigate    = useNavigate();
  const { data }    = useEmployees({ limit: 200, status: 'ACTIVE' });
  const employees   = data?.data || [];
  const initiate    = useInitiateFullFinal();
  const [empSearch, setEmpSearch]   = useState('');
  const [selEmp, setSelEmp]         = useState(null);
  const [sepType, setSepType]       = useState('Resignation');
  const [resignDate, setResignDate] = useState(new Date().toISOString().slice(0, 10));
  const [lwd, setLwd]               = useState('');

  const filteredEmps = useMemo(() => {
    const q = empSearch.toLowerCase();
    return employees.filter(e => {
      const n = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      return !q || n.includes(q) || (e.employee_code || '').toLowerCase().includes(q);
    });
  }, [employees, empSearch]);

  const handleSubmit = () => {
    if (!selEmp)   { toast.error('Select an employee'); return; }
    if (!resignDate) { toast.error('Enter separation date'); return; }
    if (!lwd)        { toast.error('Enter last working day'); return; }

    const empName = `${selEmp.first_name || ''} ${selEmp.last_name || ''}`.trim();
    const deptStr = (d) => typeof d === 'string' ? d : (d?.name || '');

    initiate.mutate({
      employee_id:     selEmp.id,
      employee_name:   empName,
      employee_code:   selEmp.employee_code || '',
      department:      deptStr(selEmp.department),
      designation:     typeof selEmp.designation === 'string' ? selEmp.designation : (selEmp.designation?.name || ''),
      joining_date:    selEmp.joining_date || '',
      separation_type: sepType,
      separation_date: resignDate,
      last_working_day: lwd,
      stage:           'INITIATION',
      gross_monthly:   selEmp.salary?.gross_monthly || 0,
      basic_monthly:   selEmp.salary?.basic || 0,
    }, {
      onSuccess: (res) => {
        toast.success(`F&F initiated for ${empName}`);
        navigate(`/documents/full-final/${selEmp.id}`);
        onClose();
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <div>
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Initiate Full & Final</h3>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">Start the exit process for an employee</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Employee */}
          <div>
            <label className="form-label">Employee *</label>
            {selEmp ? (
              <div className="flex items-center gap-2.5 p-2.5 rounded-[10px] bg-[var(--accent-muted)] border border-[var(--accent)]/30">
                <Avatar name={`${selEmp.first_name || ''} ${selEmp.last_name || ''}`.trim()} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[var(--text-primary)]">{`${selEmp.first_name || ''} ${selEmp.last_name || ''}`.trim()}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">{selEmp.employee_code} · {typeof selEmp.designation === 'string' ? selEmp.designation : selEmp.designation?.name}</p>
                </div>
                <button onClick={() => setSelEmp(null)} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]"><X size={13} /></button>
              </div>
            ) : (
              <div className="border border-[var(--border)] rounded-[10px] overflow-hidden">
                <div className="relative border-b border-[var(--border)]">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                  <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search active employees…"
                    className="w-full h-9 pl-9 pr-3 text-[12px] bg-[var(--bg-app)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none" />
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {filteredEmps.slice(0, 10).map(emp => {
                    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                    return (
                      <button key={emp.id} onClick={() => { setSelEmp(emp); setEmpSearch(''); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors text-left">
                        <Avatar name={name} size="xs" />
                        <div>
                          <p className="text-[12px] font-medium text-[var(--text-primary)]">{name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{emp.employee_code}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="form-label">Separation Type *</label>
            <select value={sepType} onChange={e => setSepType(e.target.value)} className="input-field">
              {SEPARATION_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">{sepType === 'Resignation' ? 'Resignation Date' : 'Separation Date'} *</label>
              <input type="date" value={resignDate} onChange={e => setResignDate(e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="form-label">Last Working Day *</label>
              <input type="date" value={lwd} onChange={e => setLwd(e.target.value)} className="input-field" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={initiate.isPending}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
            {initiate.isPending ? 'Initiating…' : 'Initiate F&F'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FullFinalListPage() {
  const navigate  = useNavigate();
  const [modal, setModal]   = useState(false);
  const [search, setSearch] = useState('');
  const { data }            = useFullFinalList();
  const records             = data?.data || [];

  const filtered = useMemo(() =>
    records.filter(r => !search ||
      r.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.employee_code?.toLowerCase().includes(search.toLowerCase())),
    [records, search]);

  const stats = useMemo(() => ({
    total:    records.length,
    open:     records.filter(r => r.stage !== 'CLOSED').length,
    closed:   records.filter(r => r.stage === 'CLOSED').length,
  }), [records]);

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary)]">Full & Final Settlement</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">Manage employee exit process, clearances, and final salary computation</p>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors shrink-0">
          <Plus size={15} /> Initiate F&F
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total F&F Cases', value: stats.total, icon: UserX, color: 'text-[var(--text-primary)]' },
          { label: 'In Progress', value: stats.open, icon: Clock, color: 'text-amber-600' },
          { label: 'Closed', value: stats.closed, icon: CheckCircle, color: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="card p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--bg-app)] flex items-center justify-center shrink-0">
              <s.icon size={17} className={s.color} />
            </div>
            <div>
              <p className={cn('text-[20px] font-bold', s.color)}>{s.value}</p>
              <p className="text-[11px] text-[var(--text-tertiary)]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by employee name or code…"
          className="w-full h-9 pl-9 pr-3 text-[12px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)]" />
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <UserX size={32} className="text-[var(--text-tertiary)] opacity-40" />
            <p className="text-[14px] font-medium text-[var(--text-secondary)]">No F&F cases yet</p>
            <button onClick={() => setModal(true)}
              className="h-8 px-4 text-[12px] font-medium bg-[var(--accent)] text-white rounded-[8px] hover:bg-[var(--accent-hover)] transition-colors">
              Initiate First F&F
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Employee', 'Dept', 'Separation Type', 'Last Working Day', 'Days Left', 'Stage', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const daysLeft = r.last_working_day ? differenceInDays(new Date(r.last_working_day), new Date()) : null;
                  const stageMeta = STAGE_META[r.stage] || STAGE_META.INITIATION;
                  return (
                    <tr key={r.id || r.employee_id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                      onClick={() => navigate(`/documents/full-final/${r.employee_id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={r.employee_name} size="sm" />
                          <div>
                            <p className="font-medium text-[var(--text-primary)]">{r.employee_name}</p>
                            <p className="text-[10px] text-[var(--text-tertiary)]">{r.employee_code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-secondary)]">{r.department}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--bg-hover)] text-[var(--text-secondary)]">
                          {r.separation_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">
                        {r.last_working_day ? format(new Date(r.last_working_day), 'dd MMM yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {daysLeft !== null && (
                          <span className={cn('text-[11px] font-medium',
                            daysLeft < 0 ? 'text-red-500' : daysLeft <= 7 ? 'text-amber-600' : 'text-[var(--text-secondary)]')}>
                            {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', stageMeta.color)}>
                          {stageMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={e => { e.stopPropagation(); navigate(`/documents/full-final/${r.employee_id}`); }}
                          className="h-7 px-2.5 text-[11px] font-medium border border-[var(--border)] rounded-[6px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
                          {r.stage === 'CLOSED' ? 'View' : 'Continue'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <InitiateModal onClose={() => setModal(false)} />}
    </div>
  );
}
